import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Search, Loader2, Link2, Check, Edit, AlertTriangle, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { formatFactorValue } from '../lib/format';
import { EMISSION_CATEGORIES, getCategoryLabel } from '../lib/constants';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomMappingEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [originalMapping, setOriginalMapping] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    category: '',
    sub_category: '',
    type: '',
    sub_type: '',
    desired_emission_factor_id: null,
    additional_information: ''
  });

  // EF Search state
  const [efSearchCategory, setEfSearchCategory] = useState('');
  const [efSearchText, setEfSearchText] = useState('');
  const [efSearchId, setEfSearchId] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedEF, setSelectedEF] = useState(null);

  const fetchMapping = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/custom-mappings/${id}`);
      const mapping = response.data;
      
      // Allow editing for both pending and rejected mappings
      if (mapping.status !== 'pending' && mapping.status !== 'rejected') {
        toast.error('Only pending or rejected mappings can be edited');
        navigate('/custom-mappings');
        return;
      }
      
      setOriginalMapping(mapping);
      setFormData({
        category: mapping.category,
        sub_category: mapping.sub_category,
        type: mapping.type || '',
        sub_type: mapping.sub_type || '',
        desired_emission_factor_id: mapping.desired_emission_factor_id,
        additional_information: mapping.additional_information || ''
      });
      
      // Set the selected EF
      setSelectedEF({
        emission_factor_id: mapping.desired_emission_factor_id,
        category: mapping.ef_category,
        sub_category: mapping.ef_sub_category,
        type: mapping.ef_type,
        country: mapping.ef_country,
        co2e: mapping.ef_co2e
      });
    } catch (error) {
      toast.error('Failed to load custom mapping');
      navigate('/custom-mappings');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchMapping();
  }, [fetchMapping]);

  // Search emission factors with wildcard
  const searchEmissionFactors = async () => {
    if (!efSearchCategory && !efSearchText && !efSearchId) {
      toast.error('Please enter a search criteria (category, text, or EF ID)');
      return;
    }

    setSearchLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('status', 'approved');
      params.append('page_size', '200');
      if (efSearchCategory) params.append('category', efSearchCategory);

      if (efSearchId) {
        params.append('search', efSearchId);
      } else if (efSearchText) {
        params.append('search', efSearchText);
      }

      const response = await axios.get(`${API_URL}/emission-factors?${params}`);
      let results = response.data.items || [];
      
      // Additional client-side wildcard filtering for text search
      if (efSearchText && !efSearchId) {
        const searchLower = efSearchText.toLowerCase();
        results = results.filter(ef => 
          (ef.sub_category?.toLowerCase().includes(searchLower)) ||
          (ef.type?.toLowerCase().includes(searchLower)) ||
          (ef.sub_type?.toLowerCase().includes(searchLower))
        );
      }
      
      setSearchResults(results.slice(0, 50));
      
      if (results.length === 0) {
        toast.info('No emission factors found matching your criteria');
      }
    } catch (error) {
      toast.error('Failed to search emission factors');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectEF = (ef) => {
    setSelectedEF(ef);
    setFormData(prev => ({
      ...prev,
      desired_emission_factor_id: ef.emission_factor_id
    }));
  };

  const handleSubmit = async () => {
    if (!formData.category) {
      toast.error('Please select a Category');
      return;
    }
    if (!formData.sub_category) {
      toast.error('Please enter a Sub Category');
      return;
    }
    if (!formData.desired_emission_factor_id) {
      toast.error('Please select an Emission Factor');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/custom-mappings/${id}`, {
        category: formData.category,
        sub_category: formData.sub_category,
        type: formData.type || null,
        sub_type: formData.sub_type || null,
        desired_emission_factor_id: formData.desired_emission_factor_id,
        additional_information: formData.additional_information.trim() || null
      });
      const isResubmit = originalMapping?.status === 'rejected';
      toast.success(isResubmit ? 'Custom mapping resubmitted for approval' : 'Custom mapping updated successfully');
      navigate(`/custom-mappings/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update custom mapping');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="custom-mapping-edit-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/custom-mappings/${id}`)}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
              <Edit className="w-8 h-8" />
              {originalMapping?.status === 'rejected' ? 'Resubmit' : 'Edit'} Custom Mapping #{id}
            </h1>
            <p className="text-muted-foreground mt-1">
              {originalMapping?.status === 'rejected' 
                ? 'Update the mapping and resubmit for approval' 
                : 'Update the custom mapping details before approval'}
            </p>
          </div>
        </div>

        {/* Rejection Comment Alert */}
        {originalMapping?.status === 'rejected' && originalMapping?.comment && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-red-800 mb-1">Previous Rejection Reason</h3>
                  <p className="text-red-700">{originalMapping.comment}</p>
                  <p className="text-sm text-red-600 mt-2">
                    Please address the above feedback before resubmitting.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Custom Category Details */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5" />
              Custom Category Details
            </CardTitle>
            <CardDescription>Update the custom category, sub-category, type and sub-type for this mapping</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="category">Category <span className="text-red-500">*</span></Label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                >
                  <SelectTrigger id="category" data-testid="custom-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMISSION_CATEGORIES.map(opt => (
                      <SelectItem key={opt.value} value={opt.label}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_category">Sub Category <span className="text-red-500">*</span></Label>
                <Input
                  id="sub_category"
                  value={formData.sub_category}
                  onChange={(e) => setFormData(prev => ({ ...prev, sub_category: e.target.value }))}
                  placeholder="Enter sub category"
                  data-testid="custom-sub-category-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <Input
                  id="type"
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  placeholder="Enter type (optional)"
                  data-testid="custom-type-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="sub_type">Sub Type</Label>
                <Input
                  id="sub_type"
                  value={formData.sub_type}
                  onChange={(e) => setFormData(prev => ({ ...prev, sub_type: e.target.value }))}
                  placeholder="Enter sub type (optional)"
                  data-testid="custom-sub-type-input"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="additional_information">Additional Information</Label>
                <Textarea
                  id="additional_information"
                  rows={3}
                  value={formData.additional_information}
                  onChange={(e) => setFormData(prev => ({ ...prev, additional_information: e.target.value }))}
                  placeholder="Optional: why does this custom mapping belong to the selected emission factor? Approvers and auditors will read this."
                  data-testid="custom-additional-information-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* EF Search */}
        <Card className="border-gray-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Change Linked Emission Factor
            </CardTitle>
            <CardDescription>Search for a different emission factor by category, text (wildcard), or EF ID</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Filter by Category</Label>
                <Select value={efSearchCategory || "all"} onValueChange={(v) => setEfSearchCategory(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="ef-search-category">
                    <SelectValue placeholder="Any category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any category</SelectItem>
                    {EMISSION_CATEGORIES.map(opt => (
                      <SelectItem key={opt.value} value={opt.label}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Search by Text (Wildcard)</Label>
                <Input
                  value={efSearchText}
                  onChange={(e) => setEfSearchText(e.target.value)}
                  placeholder="e.g., *fiber*, cotton..."
                  data-testid="ef-search-text"
                />
              </div>
              <div className="space-y-2">
                <Label>Search by EF ID</Label>
                <Input
                  value={efSearchId}
                  onChange={(e) => setEfSearchId(e.target.value)}
                  placeholder="Enter EF ID"
                  data-testid="ef-search-id"
                />
              </div>
              <div className="space-y-2">
                <Label>&nbsp;</Label>
                <Button 
                  onClick={searchEmissionFactors} 
                  disabled={searchLoading} 
                  className="w-full"
                  data-testid="ef-search-btn"
                >
                  {searchLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                  Search
                </Button>
              </div>
            </div>

            {/* Selected EF */}
            {selectedEF && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="font-semibold text-green-800 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Currently Selected Emission Factor:
                </p>
                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-green-700">
                  <div><strong>EF ID:</strong> #{selectedEF.emission_factor_id}</div>
                  <div><strong>Category:</strong> {getCategoryLabel(selectedEF.category)}</div>
                  <div><strong>Sub Category:</strong> {selectedEF.sub_category}</div>
                  <div><strong>Type:</strong> {selectedEF.type || '—'}</div>
                  <div><strong>Country:</strong> {selectedEF.country}</div>
                  <div><strong>CO2e:</strong> {formatFactorValue(selectedEF.co2e)}</div>
                </div>
              </div>
            )}

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">EF ID</TableHead>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs">Sub Category</TableHead>
                        <TableHead className="text-xs">Type</TableHead>
                        <TableHead className="text-xs">Sub Type</TableHead>
                        <TableHead className="text-xs">Country</TableHead>
                        <TableHead className="text-xs">CO2e</TableHead>
                        <TableHead className="text-xs">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {searchResults.map((ef) => (
                        <TableRow 
                          key={ef.emission_factor_id} 
                          className={`cursor-pointer hover:bg-gray-50 ${selectedEF?.emission_factor_id === ef.emission_factor_id ? 'bg-green-50' : ''}`}
                        >
                          <TableCell className="text-xs font-mono">{ef.emission_factor_id}</TableCell>
                          <TableCell className="text-xs">{getCategoryLabel(ef.category)}</TableCell>
                          <TableCell className="text-xs">{ef.sub_category}</TableCell>
                          <TableCell className="text-xs">{ef.type || '—'}</TableCell>
                          <TableCell className="text-xs">{ef.sub_type || '—'}</TableCell>
                          <TableCell className="text-xs">{ef.country}</TableCell>
                          <TableCell className="text-xs">{formatFactorValue(ef.co2e)}</TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant={selectedEF?.emission_factor_id === ef.emission_factor_id ? 'default' : 'outline'}
                              onClick={() => handleSelectEF(ef)}
                              data-testid={`select-ef-${ef.emission_factor_id}`}
                            >
                              {selectedEF?.emission_factor_id === ef.emission_factor_id ? 'Selected' : 'Select'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="p-2 bg-gray-50 text-xs text-muted-foreground border-t">
                  Showing {searchResults.length} results (max 50)
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(`/custom-mappings/${id}`)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={submitting || !formData.category || !formData.sub_category || !formData.desired_emission_factor_id}
            className="btn-primary"
            data-testid="save-mapping-btn"
          >
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {originalMapping?.status === 'rejected' ? 'Resubmit for Approval' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
